import { Flex, Link, Spinner, Text } from "@chakra-ui/react";
import { useComicReleasesData } from "../../../screens/reader/hooks/hookstate/useComicReleasesData";
import { Comic, Release } from "../../../types";
import { ImmutableObject } from "@hookstate/core";
import { useQuery } from "@tanstack/react-query";
import {
  GET_COMIC_RELEASES_QUERY_KEY,
  getComicReleases,
} from "../../../screens/reader/api/readerScreenApi";
import { useInvalidateQueries } from "../../../services/axiosInstance";
import { SortLanguageMenu } from "../../sortMenus/SortLanguageMenu";
import { useEffect, useState } from "react";
import { useNavigateToReaderWithContext } from "../../../screens/reader/hooks/useNavigateToReaderWithContext";
import { NavigationTrigger } from "../../../screens/reader/types";
import { UnlockableReleaseCard } from "../../cards/UnlockableReleaseCard";
import { useReleaseData } from "../../../screens/reader/hooks/hookstate/useReleaseData";
import { rootComponentsStore } from "../../../hookstate/rootComponentsStore";
import { Trans, useTranslation } from "react-i18next";
import { GiftIcon } from "../../icons/GiftIcon";
import { SealCheckIcon } from "../../icons/SealCheckIcon";
import { ShoppingIcon } from "../../icons/ShoppingIcon";
import { useIsInReader } from "../../../hooks/useIsInReader";
import { ReleaseTypeBanner } from "../components/ReleaseTypeBanner";
import { useUserEntitlementQueryKey } from "../../../hooks/useUserEntitlementQueryKey";

export function ReleasesList({
  comicData,
  onLanguageChange,
}: {
  comicData: ImmutableObject<Comic> | undefined;
  onLanguageChange?: (langId: string) => void;
}) {
  const { t } = useTranslation();
  const inReader = useIsInReader();
  const { releaseData: readerReleaseData } = useReleaseData();
  const { comicReleasesData } = useComicReleasesData();
  const entitlementQueryKey = useUserEntitlementQueryKey();

  const defaultLangId = readerReleaseData?.lang_id ?? comicData?.lang;
  const [language, setLanguage] = useState<string>(defaultLangId ?? "en");

  useEffect(() => {
    onLanguageChange?.(language);
  }, [language, onLanguageChange]);

  const comicReleasesQueryKey = [
    ...GET_COMIC_RELEASES_QUERY_KEY,
    comicData?.id.toString() ?? "",
    language ?? defaultLangId ?? "",
    ...entitlementQueryKey,
  ];

  const shouldReleasesBeFetched =
    !inReader ||
    (comicReleasesData &&
      (comicData?.id !== comicReleasesData[0]?.comic_id ||
        language !== comicReleasesData[0]?.lang_id));

  const { isLoading, data } = useQuery({
    enabled: shouldReleasesBeFetched,
    queryKey: comicReleasesQueryKey,
    queryFn: () => {
      return getComicReleases(comicData?.id ?? 0, language ?? defaultLangId);
    },
    refetchOnWindowFocus: false,
  });

  const invalidateData = useInvalidateQueries(comicReleasesQueryKey);

  const releases = !shouldReleasesBeFetched
    ? comicReleasesData
    : data?.data.payload.results;

  const isListEmpty = comicData?.total_releases === 0;

  if (isListEmpty) {
    return (
      <Flex
        flexDir="column"
        bg="transparent.white.10"
        w="full"
        h="100px"
        borderRadius="10px"
        alignItems="center"
        justifyContent="center"
      >
        <Text fontSize="28px">🫣</Text>
        <Text
          fontSize="14px"
          fontWeight="500"
          fontStyle="normal"
          color="neutral.300"
        >
          {t("components.slideInMenu.comicDetailsMenu.emptyReleaseList")}
        </Text>
      </Flex>
    );
  }

  let lastReleaseWithBannerType: string | null = null;
  return (
    <Flex flexDirection="column" width="100%" height="100%" gap="24px">
      <SortLanguageMenu
        invalidate={invalidateData}
        filterState={language}
        onChange={setLanguage}
        comicLanguageIds={
          comicData?.comic_langs?.map((lang) => lang.lang_id) ?? []
        }
        initialLanguageId={comicData?.lang ?? ""}
      />
      {isLoading ? (
        <Flex alignItems="center" justifyContent="center" width="100%">
          <Spinner color="blaze.blaze" />
        </Flex>
      ) : (
        releases?.map((comicRelease) => {
          let releaseTypeBanner = null;
          if (comicRelease.is_free) {
            if (lastReleaseWithBannerType !== "free") {
              lastReleaseWithBannerType = "free";
              releaseTypeBanner = (
                <ReleaseTypeBanner
                  key={comicRelease.id}
                  text={t(
                    "components.slideInMenu.comicDetailsMenu.alwaysFreeToRead",
                  )}
                  icon={<GiftIcon />}
                  backgroundColor="success.500_10"
                  topColor="var(--chakra-colors-success-500_40)"
                  bottomColor="var(--chakra-colors-success-500_20)"
                />
              );
            }
          } else if (!comicRelease.premium_only) {
            if (lastReleaseWithBannerType !== "gold") {
              lastReleaseWithBannerType = "gold";
              releaseTypeBanner = (
                <ReleaseTypeBanner
                  key={comicRelease.id}
                  text={
                    <Trans
                      i18nKey="components.slideInMenu.comicDetailsMenu.freeWithAGoldMembership"
                      components={{
                        bold: <span style={{ fontWeight: "bold" }} />,
                      }}
                    />
                  }
                  icon={<SealCheckIcon />}
                  backgroundColor="blaze.blaze_10"
                  topColor="var(--chakra-colors-blaze-blaze_40)"
                  bottomColor="var(--chakra-colors-blaze-blaze_20)"
                />
              );
            }
          } else if (comicRelease.premium_only) {
            if (lastReleaseWithBannerType !== "premium") {
              lastReleaseWithBannerType = "premium";
              releaseTypeBanner = (
                <ReleaseTypeBanner
                  key={comicRelease.id}
                  text={
                    <Trans
                      i18nKey="components.slideInMenu.comicDetailsMenu.collectionOnlyNotIncludedWithGold"
                      components={{
                        bold: <span style={{ fontWeight: "bold" }} />,
                      }}
                    />
                  }
                  icon={<ShoppingIcon />}
                  backgroundColor="info.500_10"
                  topColor="var(--chakra-colors-info-500_40)"
                  bottomColor="var(--chakra-colors-info-500_20)"
                />
              );
            }
          }

          return (
            <>
              {releaseTypeBanner}
              <ComicReleaseCard
                key={comicRelease.id + "-card"}
                releaseData={comicRelease}
                comicData={comicData}
                activeReleaseId={readerReleaseData?.id}
              />
            </>
          );
        })
      )}
    </Flex>
  );
}

function ComicReleaseCard({
  releaseData,
  comicData,
  activeReleaseId,
}: {
  releaseData: ImmutableObject<Release>;
  comicData?: ImmutableObject<Comic>;
  activeReleaseId?: number;
}) {
  const slideInMenuConfig = rootComponentsStore.slideInMenuConfig;
  const navigationContext = slideInMenuConfig.navigationContext;

  let updatedRelease = {
    ...releaseData,
    comic: comicData,
    title: `${releaseData.title}`,
  };

  const { goToCTAUrl, navigationContext: readerNavigationContext } =
    useNavigateToReaderWithContext({
      ...navigationContext.get(),
      trigger: NavigationTrigger.releasesListCard,
      releaseToNavigate: updatedRelease as Release,
    });

  return (
    <Link
      onClick={goToCTAUrl}
      key={releaseData.id}
      id={`release-${releaseData.id}`}
      aria-label={`Read ${releaseData.title}`}
    >
      <UnlockableReleaseCard
        fullWidth
        activeBorder={releaseData?.id === activeReleaseId}
        navigationContext={readerNavigationContext}
        release={updatedRelease as Release}
        display={{
          include_last_updated: true,
          include_total_pages: true,
          include_total_views: true,
          include_progress_meter: true,
        }}
      />
    </Link>
  );
}
